import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type { RecordData, RequestError, Service } from 'webapp-types';
import { BillingFeature, ModelName, PopupEvent } from 'webapp/enums/enums';
import { redirect, sortBy } from 'webapp/utils/common';

import AccountClientPopupCreateComponent from 'webapp/components/account/client/popup-create';
import HomePopupPickClientComponent from 'webapp/components/home/popup-pick-client';

import Space from 'webapp/models-custom/space';
import type SpaceRecord from 'webapp/models/space';

import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';
import type { TagCountList } from 'webapp/services/model-manager/tags';

export type TrackingOriginTypeForNewClientForm =
	'clients' | 'client_performance' | 'client_kpis' | 'sidebar' | 'setup';

export default class ModelManagerSpacesService extends ModelManagerForStoreService<
	SpaceRecord,
	Space
> {
	@service('account') declare accountService: Service.Account;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/tags') declare mmTagsService: Service.ModelManager.Tags;
	@service('popup') declare popupService: Service.Popup;
	@service('billing') declare billingService: Service.Billing;

	@cached
	get agencySpaceRecord() {
		let agencyRecord;
		if (
			this.accountService.isAgency ||
			this.accountService.isClient ||
			this.accountService.hasPartnerResellerId
		) {
			agencyRecord = this.savedRecords.find((s) => s.isEnterprise);
		}
		return agencyRecord;
	}

	get agencySpaceModel(): Space | null {
		const agencyRecord = this.agencySpaceRecord;
		return agencyRecord ? this.createModelFromRecord(agencyRecord) : null;
	}

	@cached
	get otherSpaceRecords() {
		let spaceRecords: SpaceRecord[] = [];
		if (this.agencySpaceRecord) {
			spaceRecords = sortBy(
				this.savedRecords.filter((space) => {
					return !space.isEnterprise && !space.hidden;
				}),
				'order',
				'id',
			);
		}
		return spaceRecords;
	}

	get otherSpaceModels(): Space[] {
		return this.createModelsFromRecordsArray(this.otherSpaceRecords);
	}

	get assignedTags(): TagCountList[] {
		const tags = this.otherSpaceModels.reduce((acc: number[], model: Space) => {
			acc.push(...model.tags);
			return acc;
		}, []);
		return this.mmTagsService.getTagCountList(tags);
	}

	get isMultipleSpaces() {
		return !!this.agencySpaceRecord && this.otherSpaceRecords.length > 0;
	}

	// Home's Client widget is agency-only: the agency space picks any of its clients. Any other space,
	// a partner reseller included, gets none.
	@cached
	get clientRecordsToPick(): SpaceRecord[] {
		return this.agencySpaceRecord?.id === String(this.accountService.id)
			? this.otherSpaceRecords
			: [];
	}

	get clientModelsToPick(): Space[] {
		return this.createModelsFromRecordsArray(sortBy(this.clientRecordsToPick, 'companyName'));
	}

	get hasClientsToPick(): boolean {
		return this.clientRecordsToPick.length > 0;
	}

	getBySpaceId(spaceId: string): Space | null {
		const space = this.savedRecords.find((s) => s.id === spaceId);
		return space ? this.createModelFromRecord(space) : null;
	}

	createNewClient(): Space {
		const properties = {
			ownerId: this.currentUserService.numericId!,
		};
		return this.createNewModel(properties);
	}

	saveClients(clients: Space[], origin?: string): Promise<void> {
		const layer = this.infolayerService.loading('Saving accounts...'),
			request = this.requestService,
			mappedClients = clients.map((client) => ({
				ownerId: client.ownerId,
				name: client.companyName,
				websiteUrl: client.websiteUrl,
			})),
			data = {
				clients: mappedClients,
				origin,
			};

		return request
			.apiWithAsyncReply<RecordData.Payload>('POST', '/spaces/batch', data)
			.then(
				(payload) => {
					this.storeService.pushPayload(payload);
					layer.ok('Accounts saved!');
				},
				(err) => {
					layer.close();
					throw err;
				},
			)
			.catch((err: RequestError) => {
				request.showAllValidationErrors(err);
				return Promise.reject();
			});
	}

	redirectToAgencySpace() {
		const agencySpace = this.agencySpaceModel;
		if (agencySpace) {
			redirect(agencySpace.loginUrl);
		}
	}

	openCreateClientPopup(origin: TrackingOriginTypeForNewClientForm) {
		if (!this.accountService.canAddClient) {
			return;
		}

		if (!this.billingService.checkIfCan(BillingFeature.CLIENTS, true)) {
			return;
		}

		const popup = this.popupService.createModal(AccountClientPopupCreateComponent, {
			popupClass: 'popup-type-create-client',
			allowStacked: true, // for user invite
		});

		popup.open({
			model: this.createNewClient(),
			origin,
		});

		return popup;
	}

	// Home's client picker: the picked client space id, or null when the picker closes or can't open.
	pickClient(): Promise<number | null> {
		return new Promise((resolve) => {
			const popup = this.popupService.createModal(HomePopupPickClientComponent, {
				popupClass: 'popup-type-home-pick-client',
			});
			// CLOSED follows every close, even one while still opening; after a submit it is a no-op.
			popup.on(PopupEvent.CLOSED, this, () => resolve(null));

			// A refused open (popups disallowed, the onboarding banner, a picker already open) fires no event.
			if (!popup.open({ onSubmit: resolve })) {
				resolve(null);
			}
		});
	}

	// ------ For-store ------

	modelName = ModelName.SPACE;
	override modelTitle = 'account';

	createModel(model: SpaceRecord): Space {
		return new Space({ model });
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/spaces': ModelManagerSpacesService;
	}
}
